import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Alert, 
  Spinner,
  Badge,
  Pagination
} from 'react-bootstrap';
import { FiEdit2, FiTrash2, FiUserCheck, FiUserX, FiPlus } from 'react-icons/fi';
import { usersAPI, customersAPI, systemsAPI } from '../services/api';
import { User, UsersResponse } from '../types';
import { useAuth } from '../contexts/AuthContext';

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [customers, setCustomers] = useState<any[]>([]);
  const [systems, setSystems] = useState<any[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    loginId: '',
    password: '',
    designation: '',
    department: '',
    customerId: '',
    assignedSystems: [] as string[]
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response: UsersResponse = await usersAPI.getAll(currentPage, 10);
      setUsers(response.users);
      setTotalPages(response.pagination.total);
    } catch (error) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      loginId: '',
      password: '',
      designation: '',
      department: '',
      customerId: '',
      assignedSystems: []
    });
    setEditingUser(null);
  };

  const handleShowModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        loginId: user.loginId,
        password: '',
        designation: user.designation || '',
        department: user.department || '',
        customerId: typeof user.customerId === 'object' ? (user.customerId as any).id || (user.customerId as any)._id : (user.customerId as any) || '',
        assignedSystems: Array.isArray((user as any).assignedSystems) ? (user as any).assignedSystems.map((s: any) => s.id || s._id) : []
      });
    } else {
      resetForm();
    }

    // If current user is superadmin, fetch customers for selector when opening modal
    if (currentUser?.role === 'superadmin') {
      (async () => {
        try {
          const res = await customersAPI.getAll(1, 1000);
          setCustomers(res.customers || []);
          // if editing existing user, fetch systems for that customer
          if (user) {
            const cid = typeof user.customerId === 'object' ? (user.customerId as any).id || (user.customerId as any)._id : (user.customerId as any) || '';
            if (cid) {
              const sysRes = await systemsAPI.getAll(1, 1000, '', cid as string);
              setSystems(sysRes.systems || []);
            }
          }
        } catch (err) {
          // ignore
        }
      })();
    }

    // If current user is a customer, fetch systems for their customer on modal open
    if (currentUser?.role === 'customer') {
      (async () => {
        try {
          const cid = (currentUser as any).id || (currentUser as any).customerId || '';
          const sysRes = await systemsAPI.getAll(1, 1000, '', cid as string);
          setSystems(sysRes.systems || []);
        } catch (err) {
          // ignore
        }
      })();
    }

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const { name } = target;
    // handle multiple select for assignedSystems
    if (target instanceof HTMLSelectElement && target.multiple) {
      const values = Array.from(target.selectedOptions).map(o => o.value);
      setFormData(prev => ({ ...prev, [name]: values }));
      return;
    }
    const { value } = target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const payload: any = {
        ...formData,
        isActive: true
      };

      // Only superadmin should send customerId; customers will be associated server-side
      if (currentUser?.role !== 'superadmin') {
        delete payload.customerId;
      }

      if (editingUser) {
        const uid = (editingUser as any).id || (editingUser as any)._id;
        await usersAPI.update(uid, payload);
        setSuccess('User updated successfully');
      } else {
        await usersAPI.create(payload);
        setSuccess('User created successfully');
      }
      
      fetchUsers();
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (error: any) {
      setError(error.message || 'Operation failed');
    }
  };

  // When superadmin selects a customer, load systems for that customer
  useEffect(() => {
    if (currentUser?.role === 'superadmin' && formData.customerId) {
      (async () => {
        try {
          const res = await systemsAPI.getAll(1, 1000, '', formData.customerId);
          setSystems(res.systems || []);
        } catch (err) {
          setSystems([]);
        }
      })();
    }
  }, [formData.customerId, currentUser]);

  const handleToggleStatus = async (userId: string) => {
    try {
      await usersAPI.toggleStatus(userId);
      fetchUsers();
    } catch (error) {
      setError('Failed to toggle user status');
    }
  };

  const handleDelete = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await usersAPI.delete(userId);
        fetchUsers();
      } catch (error) {
        setError('Failed to delete user');
      }
    }
  };

  if (loading && users.length === 0) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem', color: '#667eea' }}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading users...</p>
      </Container>
    );
  }

  // Only customers and super admins can create users
  const canCreateUsers = currentUser?.role === 'customer' || currentUser?.role === 'superadmin';

  return (
    <Container fluid className="py-3">
      <div style={{ width: '95%', maxWidth: '1600px', margin: '0 auto' }}>
      <Row className="mb-4">
        <Col>
          <div className="gradient-card p-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <h2 className="mb-2 fw-bold">Users Management</h2>
              <p className="mb-0 opacity-90">Manage user accounts and permissions</p>
            </div>
            {canCreateUsers && (
              <Button variant="primary" onClick={() => handleShowModal()} className="btn-gradient btn-gradient-primary">
                <FiPlus size={16} className="me-2" />Add New User
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')} className="modern-card">{error}</Alert>}

      <Row>
        <Col>
          <Card className="modern-card">
            <Card.Body>
              <div className="table-responsive">
                <Table className="modern-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Login ID</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Designation</th>
                      <th>Department</th>
                      {currentUser?.role === 'superadmin' && <th>Company</th>}
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => {
                      const uid = (user as any).id || (user as any)._id;
                      return (
                      <tr key={uid}>
                        <td>{user.name}</td>
                        <td>{user.loginId}</td>
                        <td>{user.email}</td>
                        <td>{user.phone || 'N/A'}</td>
                        <td>{user.designation || 'N/A'}</td>
                        <td>{user.department || 'N/A'}</td>
                        {currentUser?.role === 'superadmin' && (
                          <td>{typeof user.customerId === 'object' ? user.customerId.companyName : 'N/A'}</td>
                        )}
                        <td>
                          <Badge bg={user.isActive ? 'success' : 'danger'}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowModal(user)}
                            title="Edit user"
                            aria-label="Edit user"
                          >
                            <FiEdit2 size={14} />
                          </Button>
                          {canCreateUsers && (
                            <>
                              <Button
                                variant={user.isActive ? 'outline-warning' : 'outline-success'}
                                size="sm"
                                className="me-1"
                                onClick={() => handleToggleStatus(uid)}
                              >
                                {user.isActive ? <><FiUserX size={14} className="me-1" /></> : <><FiUserCheck size={14} className="me-1" />Activate</>}
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(uid)}
                                title="Delete user"
                                aria-label="Delete user"
                              >
                                <FiTrash2 size={14} />
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </Table>
              </div>

              {users.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted">
                    {canCreateUsers 
                      ? 'No users found. Click "Add New User" to create one.'
                      : 'No users found.'
                    }
                  </p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.Prev 
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    />
                    {[...Array(totalPages)].map((_, index) => (
                      <Pagination.Item
                        key={index + 1}
                        active={index + 1 === currentPage}
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* User Form Modal */}
      {canCreateUsers && (
        <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
          <Modal.Header closeButton className="gradient-card-header border-0">
            <Modal.Title>
              {editingUser ? 'Edit User' : 'Add New User'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body className="p-4">
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              
              {currentUser?.role === 'superadmin' && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Customer *</Form.Label>
                      <Form.Select
                        name="customerId"
                        value={formData.customerId}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select customer</option>
                        {customers.map((c) => (
                          <option key={c.id || c._id} value={c.id || c._id}>{c.companyName}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {currentUser?.role === 'customer' && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Customer</Form.Label>
                      <Form.Control
                        type="text"
                        value={(currentUser as any).companyName || ''}
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {(currentUser?.role === 'superadmin' || currentUser?.role === 'customer') && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Assign Systems</Form.Label>
                      <Form.Select
                        name="assignedSystems"
                        value={formData.assignedSystems as any}
                        onChange={handleInputChange}
                        multiple
                      >
                        {systems.map((s) => (
                          <option key={s.id || s._id} value={s.id || s._id}>{s.systemName} ({s.systemCode})</option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">Optional — assign systems this user can access</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Login ID *</Form.Label>
                    <Form.Control
                      type="text"
                      name="loginId"
                      value={formData.loginId}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email *</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone *</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Designation</Form.Label>
                    <Form.Control
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Department</Form.Label>
                    <Form.Control
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Password {!editingUser && '*'}</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  placeholder={editingUser ? 'Leave empty to keep current password' : ''}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className="border-0">
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" className="btn-gradient btn-gradient-primary">
                {editingUser ? 'Update User' : 'Create User'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}
      </div>
    </Container>
  );
};

export default Users;